// ─────────────────────────────────────────────
//  saveNextRoom — persist "where this team goes next".
//
//  Lifted out of NextRoomModal so the web modal and the mobile sheet perform
//  the IDENTICAL sequence. The write is deceptively multi-step (tear down the
//  previously staged card, link, then materialise an empty destination), and a
//  second hand-written copy on the mobile side would have drifted on the very
//  first edge case — the orphan teardown.
//
//  The link itself is ONE field pair on the source row (nextRoomId /
//  nextRoomName); the destination card derives its own "incoming" arrow from
//  that row (domain/nextRoom), so the two cards can never disagree.
//
//  Returns { destName, staged } so the caller can word its own confirmation:
//  `staged` is true when an empty destination was materialised.
// ─────────────────────────────────────────────
import {
  updateAssignmentRow,
  createAssignment,
  deleteAssignmentRow,
} from "../../firebase/firestore";
import { assignmentForRoom } from "../../domain/gridTemplate";
import { rosterDateKey } from "../../hooks/useDailyRoster";

export async function saveNextRoom({
  assignment,
  room,
  destId,
  choices = [],
  assignments = [],
}) {
  if (!assignment?.id) throw new Error("no-assignment");
  const dest = choices.find((c) => c.id === destId) || null;

  // Link change/clear: a row THIS flow previously staged on the OLD
  // destination (source "next-room", same team) is torn down so no orphan
  // card lingers on the board. Rows the flow did not create are left alone —
  // a real scheduled case must never be deleted by re-routing a team.
  if (assignment.nextRoomName && assignment.nextRoomId !== destId) {
    const prevRow = assignmentForRoom(assignment.nextRoomName, assignments);
    if (
      prevRow?.id &&
      prevRow.id !== assignment.id &&
      prevRow.source === "next-room" &&
      (prevRow.ahpId || null) === (assignment.ahpId || null)
    ) {
      await deleteAssignmentRow(prevRow.id);
    }
  }

  await updateAssignmentRow(assignment.id, {
    nextRoomId: destId || null,
    nextRoomName: dest?.name || "",
  });

  // Empty destination → stage a brand-new card there with THIS team, so the
  // board materialises the room (a room with no assignment data does not
  // render) and its message slot reads "Coming from: <this room>". Occupied
  // destinations just link.
  let staged = false;
  if (dest && !dest.inUse) {
    const existingDest = assignmentForRoom(dest.name, assignments);
    const teamFields = {
      attendingId: assignment.attendingId || null,
      attendingName: assignment.attendingName || "",
      ahpId: assignment.ahpId || null,
      ahpName: assignment.ahpName || "",
      ahpShiftCode: assignment.ahpShiftCode ?? null,
      source: "next-room",
    };
    if (existingDest?.id) {
      await updateAssignmentRow(existingDest.id, teamFields);
    } else {
      await createAssignment({
        facilityId: assignment.facilityId || room?.facilityId || "default",
        date: assignment.date || rosterDateKey(),
        orRoom: String(dest.name).replace(/^OR\s+/i, ""),
        surgeon: "",
        startTime: "",
        endTime: "",
        ahpDepartTime: "",
        ...teamFields,
      });
    }
    staged = true;
  }

  return { destName: dest?.name || "", staged };
}

export default saveNextRoom;
